import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ContentStatus, CreoleDictionaryEntry, FolkArtForm, FolkArtRegistryEntry, OrthographyCategory, OrthographyResource, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, SectionCard, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TABS = [
  { key: 'dictionary', label: 'Dictionary' },
  { key: 'orthography', label: 'Orthography' },
  { key: 'folkart', label: 'Folk art' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const STATUS_OPTIONS: ContentStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

export function CreolePage() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('ARCHIVIST') ?? false;
  const [tab, setTab] = useState<TabKey>('dictionary');

  return (
    <div>
      <PageHeader title="CreolePortal CMS" subtitle="Kreol Seselwa dictionary, orthography resources, and the folk-art registry — creole.ts." />
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Signed in without the Archivist role — showing PUBLISHED content only. Drafting, editing, and publishing require ARCHIVIST." />
        </div>
      ) : null}
      {tab === 'dictionary' ? <DictionaryTab canManage={canManage} /> : null}
      {tab === 'orthography' ? <OrthographyTab canManage={canManage} /> : null}
      {tab === 'folkart' ? <FolkArtTab canManage={canManage} /> : null}
    </div>
  );
}

function StatusActions({ status, onChange, busy }: { status: ContentStatus; onChange: (s: ContentStatus) => void; busy: boolean }) {
  return (
    <select
      className="field-input !w-auto !py-1 text-xs"
      value={status}
      onChange={(e) => onChange(e.target.value as ContentStatus)}
      disabled={busy}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

// ── Dictionary ─────────────────────────────────────────────────────────

function DictionaryTab({ canManage }: { canManage: boolean }) {
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const entriesQ = useApiQuery<Page<CreoleDictionaryEntry>>(`/api/v1/m30/creole/dictionary?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`);

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <input className="field-input max-w-xs" placeholder="Search headword…" value={q} onChange={(e) => setQ(e.target.value)} />
        {canManage ? (
          <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
            + New entry
          </button>
        ) : null}
      </div>
      {entriesQ.error ? <ErrorNote message={entriesQ.error} /> : null}
      <div className="card overflow-x-auto">
        {entriesQ.loading ? (
          <Spinner />
        ) : (entriesQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No dictionary entries" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Headword</th>
                <th className="table-head">Definition (EN)</th>
                <th className="table-head">Status</th>
                {canManage ? <th className="table-head" /> : null}
              </tr>
            </thead>
            <tbody>
              {entriesQ.data!.items.map((e) => (
                <DictionaryRow key={e.id} entry={e} canManage={canManage} onChanged={entriesQ.reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateDictionaryModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            entriesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DictionaryRow({ entry, canManage, onChanged }: { entry: CreoleDictionaryEntry; canManage: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const changeStatus = async (status: ContentStatus) => {
    setBusy(true);
    try {
      await api.post(`/api/v1/m30/creole/dictionary/${entry.id}/status`, { status });
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className="hover:bg-slate-50">
      <td className="table-cell font-medium">
        {entry.headword}
        {entry.exampleSentence ? <div className="text-[11px] font-normal italic text-slate-400">{entry.exampleSentence}</div> : null}
      </td>
      <td className="table-cell text-xs text-slate-500">{entry.definitionEn}</td>
      <td className="table-cell">
        <StatusBadge status={entry.status} />
      </td>
      {canManage ? (
        <td className="table-cell text-right">
          <StatusActions status={entry.status} onChange={changeStatus} busy={busy} />
        </td>
      ) : null}
    </tr>
  );
}

function CreateDictionaryModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [headword, setHeadword] = useState('');
  const [partOfSpeech, setPartOfSpeech] = useState('');
  const [definitionEn, setDefinitionEn] = useState('');
  const [definitionFr, setDefinitionFr] = useState('');
  const [exampleSentence, setExampleSentence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/creole/dictionary', {
        headword,
        partOfSpeech: partOfSpeech || undefined,
        definitionEn,
        definitionFr: definitionFr || undefined,
        exampleSentence: exampleSentence || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create entry');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New dictionary entry" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Headword">
            <input className="field-input" value={headword} onChange={(e) => setHeadword(e.target.value)} placeholder="e.g. lanmè" />
          </Field>
          <Field label="Part of speech">
            <input className="field-input" value={partOfSpeech} onChange={(e) => setPartOfSpeech(e.target.value)} placeholder="e.g. noun" />
          </Field>
        </div>
        <Field label="Definition (English)">
          <input className="field-input" value={definitionEn} onChange={(e) => setDefinitionEn(e.target.value)} />
        </Field>
        <Field label="Definition (French, optional)">
          <input className="field-input" value={definitionFr} onChange={(e) => setDefinitionFr(e.target.value)} />
        </Field>
        <Field label="Example sentence (optional)">
          <input className="field-input" value={exampleSentence} onChange={(e) => setExampleSentence(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || headword.trim().length < 1 || definitionEn.trim().length < 1}>
            {busy ? 'Creating…' : 'Create entry (DRAFT)'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Orthography ────────────────────────────────────────────────────────

const ORTHOGRAPHY_CATEGORIES: OrthographyCategory[] = ['GRAMMAR', 'SPELLING', 'GUIDE', 'CURRICULUM', 'OTHER'];

function OrthographyTab({ canManage }: { canManage: boolean }) {
  const [showCreate, setShowCreate] = useState(false);
  const resQ = useApiQuery<Page<OrthographyResource>>('/api/v1/m30/creole/orthography?limit=100');

  return (
    <div>
      <div className="mb-3 flex justify-end">
        {canManage ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New resource
          </button>
        ) : null}
      </div>
      {resQ.error ? <ErrorNote message={resQ.error} /> : null}
      <div className="card overflow-x-auto">
        {resQ.loading ? (
          <Spinner />
        ) : (resQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No orthography resources" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">Category</th>
                <th className="table-head">Status</th>
                {canManage ? <th className="table-head" /> : null}
              </tr>
            </thead>
            <tbody>
              {resQ.data!.items.map((r) => (
                <OrthographyRow key={r.id} resource={r} canManage={canManage} onChanged={resQ.reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateOrthographyModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            resQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function OrthographyRow({ resource, canManage, onChanged }: { resource: OrthographyResource; canManage: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const changeStatus = async (status: ContentStatus) => {
    setBusy(true);
    try {
      await api.post(`/api/v1/m30/creole/orthography/${resource.id}/status`, { status });
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className="hover:bg-slate-50">
      <td className="table-cell">
        <div className="font-medium">{resource.title}</div>
        {resource.url ? (
          <a className="text-[11px] text-brand-600 underline" href={resource.url} target="_blank" rel="noreferrer">
            {resource.url}
          </a>
        ) : null}
      </td>
      <td className="table-cell text-xs text-slate-500">{resource.category}</td>
      <td className="table-cell">
        <StatusBadge status={resource.status} />
      </td>
      {canManage ? (
        <td className="table-cell text-right">
          <StatusActions status={resource.status} onChange={changeStatus} busy={busy} />
        </td>
      ) : null}
    </tr>
  );
}

function CreateOrthographyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<OrthographyCategory>('GUIDE');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/creole/orthography', { title, description: description || undefined, category, url: url || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create resource');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New orthography resource" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Lortograf Kreol Seselwa — Gid Ofisyel" />
        </Field>
        <Field label="Category">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as OrthographyCategory)}>
            {ORTHOGRAPHY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="URL (optional)">
          <input className="field-input" value={url} onChange={(e) => setUrl(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.trim().length < 2}>
            {busy ? 'Creating…' : 'Create resource (DRAFT)'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Folk art ───────────────────────────────────────────────────────────

const FOLK_ART_FORMS: FolkArtForm[] = ['MUSIC', 'DANCE', 'STORYTELLING', 'CRAFT', 'CUISINE', 'FESTIVAL', 'OTHER'];

function FolkArtTab({ canManage }: { canManage: boolean }) {
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const entriesQ = useApiQuery<Page<FolkArtRegistryEntry>>(`/api/v1/m30/creole/folk-art?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`);

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <input className="field-input max-w-xs" placeholder="Search title…" value={q} onChange={(e) => setQ(e.target.value)} />
        {canManage ? (
          <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
            + New entry
          </button>
        ) : null}
      </div>
      {entriesQ.error ? <ErrorNote message={entriesQ.error} /> : null}
      <div className="card overflow-x-auto">
        {entriesQ.loading ? (
          <Spinner />
        ) : (entriesQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No folk-art entries" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">Form</th>
                <th className="table-head">Status</th>
                {canManage ? <th className="table-head" /> : null}
              </tr>
            </thead>
            <tbody>
              {entriesQ.data!.items.map((e) => (
                <FolkArtRow key={e.id} entry={e} canManage={canManage} onChanged={entriesQ.reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateFolkArtModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            entriesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FolkArtRow({ entry, canManage, onChanged }: { entry: FolkArtRegistryEntry; canManage: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const changeStatus = async (status: ContentStatus) => {
    setBusy(true);
    try {
      await api.post(`/api/v1/m30/creole/folk-art/${entry.id}/status`, { status });
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className="hover:bg-slate-50">
      <td className="table-cell">
        <div className="font-medium">{entry.title}</div>
        <div className="text-[11px] text-slate-400">{entry.practitionerName ?? '—'}</div>
      </td>
      <td className="table-cell text-xs text-slate-500">{entry.artForm}</td>
      <td className="table-cell">
        <StatusBadge status={entry.status} />
      </td>
      {canManage ? (
        <td className="table-cell text-right">
          <StatusActions status={entry.status} onChange={changeStatus} busy={busy} />
        </td>
      ) : null}
    </tr>
  );
}

function CreateFolkArtModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [artForm, setArtForm] = useState<FolkArtForm>('MUSIC');
  const [practitionerName, setPractitionerName] = useState('');
  const [district, setDistrict] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/creole/folk-art', {
        title,
        artForm,
        practitionerName: practitionerName || undefined,
        district: district || undefined,
        description,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create entry');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New folk-art registry entry" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Moutya" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Art form">
            <select className="field-input" value={artForm} onChange={(e) => setArtForm(e.target.value as FolkArtForm)}>
              {FOLK_ART_FORMS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
          <Field label="District (optional)">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
        </div>
        <Field label="Practitioner(s) (optional)">
          <input className="field-input" value={practitionerName} onChange={(e) => setPractitionerName(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.trim().length < 2 || description.trim().length < 5}>
            {busy ? 'Creating…' : 'Create entry (DRAFT)'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
